<template>
  <div>
    <div v-if="labelProp" class="mb-1 font-weight-bold">
      {{ labelProp }}<span v-if="isRequiredProb" style="color: red">*</span>
    </div>
    <div class="textarea">
      <v-textarea
        v-bind="$attrs"
        :counter="maxLengthProp !== 0"
        :disabled="disabledProp"
        :hide-details="maxLengthProp === 0"
        :placeholder="placeholderProp"
        :rows="rowsProp"
        :rules="[rules.maxLength]"
        :value="content"
        auto-grow
        outlined
        @blur="finishEditMessage"
        @input="updateValue"
        @click:append="finishEditMessage"
      >
      </v-textarea>
      <div v-if="hasIconProp" :class="isRTL ? 'iconRtl' : 'icon'">
        <v-icon class="" @click="onClick">{{ iconProp }}</v-icon>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, createComponent, PropType, value, Wrapper } from "vue-function-api";

export default createComponent({
  name: "KtznAdminTextArea",
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: "",
    },
    value: {
      type: String,
      default: "",
    },
    hasIcon: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: "mdi-pencil",
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 1,
    },
    maxLength: {
      type: Number,
      default: 0,
    },
  } as any as PropType<{
    label: string;
    value: string;
    hasIcon: boolean;
    icon: string;
    placeholder: string;
    disabled: boolean;
    rows: number;
    maxLength: number;
    isRequired: boolean;
  }>,
  setup(props, context) {
    // Props as computed
    const labelProp: Wrapper<string> = computed(() => props.label);
    const placeholderProp: Wrapper<string> = computed(() => props.placeholder);
    const disabledProp: Wrapper<boolean> = computed(() => props.disabled);
    const iconProp: Wrapper<string> = computed(() => props.icon);
    const hasIconProp: Wrapper<boolean> = computed(() => props.hasIcon);
    const rowsProp: Wrapper<number> = computed(() => props.rows);
    const maxLengthProp: Wrapper<number> = computed(() => Number(props.maxLength));
    const isRequiredProb: Wrapper<boolean> = computed(() => props.isRequired);

    //Computed Properties
    const contentState: Wrapper<string> = computed(
      () => props.value,
      (val) => {
        return val;
      }
    );
    const content: Wrapper<string> = computed(
      () => contentState.value,
      (val) => (contentState.value = val)
    );

    const isRTL: Wrapper<boolean> = computed(() =>
      ["ar", "fa", "iw", "ur", "ps", "sd", "yi"].includes(document.documentElement.lang)
    );

    const dialog: Wrapper<boolean> = value(false);
    const isEditing: Wrapper<boolean> = value(false);

    // Actions
    const finishEditMessage = () => {
      content.value = content.value.replace("%", "％");
      return (isEditing.value = false);
    };

    const updateValue = (text: string) => {
      context.emit("input", text);
    };

    const onClick = () => context.emit("click");

    return {
      onClick,
      updateValue,
      finishEditMessage,
      dialog,
      content,
      isRTL,
      iconProp,
      isEditing,
      labelProp,
      hasIconProp,
      disabledProp,
      placeholderProp,
      rowsProp,
      maxLengthProp,
      isRequiredProb,
      rules: {
        maxLength: () =>
          Number(maxLengthProp.value) !== 0
            ? content.value.length <= Number(maxLengthProp.value) || `Max ${maxLengthProp.value.toString()} characters`
            : true,
      },
    };
  },
});
</script>

<style scoped>
.textarea {
  position: relative;
}

.icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.iconRtl {
  position: absolute;
  top: 1rem;
  left: 1rem;
}
</style>
